import { AcknowledgedConsents, useSignConsent } from "../hooks"
import { Alert, Box, Breadcrumb, Breadcrumbs, Divider, ForwardIcon, Typography } from "@carrotfertility/carotene-core"
import { Form, FormButton, FormCheckbox } from "@carrotfertility/carotene-core-x"
import React from "react"

type ConsentFormValues = {
  telehealthConsent: boolean
  healthInfoAuthorization: boolean
}

type ConsentPageProps = {
  onContinue: () => void
  onReturnToProviderResults?: () => void
}

// The consents this page displays; sent to the BFF on submit. Both checkboxes below
// are `required`, so the form can't submit unless both are accepted — which is why
// this static list is safe to send unconditionally. Revisit if a consent ever becomes
// optional. Typed against the client's input, so drifting from the contract fails to
// compile.
const ACKNOWLEDGED_CONSENTS: AcknowledgedConsents = ["telehealth_visit", "health_information_sharing"]

// Literal strings (no react-intl): this package isn't in the intl extraction
// pipeline and the experience is English-only for the foreseeable future.
export function ConsentPage({ onContinue, onReturnToProviderResults }: ConsentPageProps): JSX.Element {
  const signConsent = useSignConsent()

  // The form only submits once both required boxes are checked; record consent and
  // advance only if it succeeds (errors surface via signConsent.isError below).
  const handleSubmit = () => signConsent.mutateAsync(ACKNOWLEDGED_CONSENTS, { onSuccess: onContinue })

  return (
    <Form<ConsentFormValues>
      onSubmit={handleSubmit}
      defaultValues={{ telehealthConsent: false, healthInfoAuthorization: false }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.md),
          maxInlineSize: 640
        })}
      >
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Breadcrumbs>
            <Breadcrumb component="button" type="button" onClick={onReturnToProviderResults}>
              Provider results
            </Breadcrumb>
            <Breadcrumb currentPage>Consent</Breadcrumb>
          </Breadcrumbs>
          <Divider />
        </Box>

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xs) })}
        >
          <Typography variant="h1">Consent to telehealth services</Typography>
          <Typography>Please read and agree to the following before booking your appointment.</Typography>
        </Box>

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.md) })}
        >
          <Box
            component="section"
            sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xs) })}
          >
            <Typography variant="h5" component="h2">
              Telehealth visit
            </Typography>
            <Typography>
              Your appointment will take place over a secure HIPAA-compliant video call. By agreeing, you consent to
              receiving care through this format and understand that your provider may determine an in-person visit is
              more appropriate for your needs.
            </Typography>
          </Box>
          <Box
            component="section"
            sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xs) })}
          >
            <Typography variant="h5" component="h2">
              Your health information
            </Typography>
            <Typography>
              Information you share during intake and your visit will be used to provide care and stored securely in
              accordance with HIPAA. It won&apos;t be shared with your employer.
            </Typography>
          </Box>
          <Box
            component="section"
            sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xs) })}
          >
            <Typography variant="h5" component="h2">
              Payment and charges
            </Typography>
            <Typography>
              Your card will be authorized at the time of booking and charged only after your appointment is complete.
              If you use insurance, your final cost may differ from the initial estimate based on your plan&apos;s
              adjudication.
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <FormCheckbox
            name="telehealthConsent"
            label="I consent to a telehealth visit and understand that care is delivered by independent, board-certified clinicians."
            controllerProps={{ rules: { required: "Please confirm your consent to a telehealth visit." } }}
          />
          <FormCheckbox
            name="healthInfoAuthorization"
            label="I authorize Carrot to share my relevant health and benefit information with the clinical team for this visit."
            controllerProps={{
              rules: { required: "Please authorize sharing your information with the clinical team." }
            }}
          />
        </Box>

        {signConsent.isError && (
          <Alert severity="error">Something went wrong saving your consent. Please try again.</Alert>
        )}

        <Box>
          <FormButton type="submit" color="secondary" endIcon={<ForwardIcon />}>
            Continue
          </FormButton>
        </Box>
      </Box>
    </Form>
  )
}
